import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import _ from 'lodash';

import { Col, Row, Button, Checkbox } from 'antd';

import { PitstopModal } from 'shared';
import UsersTable from '../UsersTable';
import TableStore from 'stores/abstract/TableStore';
import { AppStore, CurrentUserStore, ShopStore } from 'stores';
import { observer } from 'mobx-react';
import SuccessModal from './SuccessModal';
import { toJS } from 'mobx';
import PropTypes from 'prop-types';

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-header {
      margin-bottom: 30px;
      .ant-modal-title {
        color: #02103d;
      }
    }

    .ant-table-header {
      background-color: transparent !important;
    }

    .ant-table {
      max-width: 800px !important;
      table-layout: fixed !important;
    }
  }
`;

const initialState = {
  notificationType: [],
  actions: [],
  users: [],
  saving: false,
};

const PMNotificationsModal = (props) => {
  const { title, modalId } = props;

  const [savedState, setSavedState] = useState(initialState);
  const [state, setState] = useState(initialState);

  const PM_ALLERT_SUCCESS_MODAL_ID = 'PM_ALLERT_SUCCESS_MODAL_ID';
  const ADD_PM_NOTIFICATIONS_MODAL_ID = 'ADD_PM_NOTIFICATIONS_MODAL_ID';
  const EDIT_ISSUE_ALLERT_MODAL_ID = 'EDIT_ISSUE_ALLERT_MODAL_ID';

  const userTableRef = useRef();
  const [tableStore] = useState(() => new TableStore());

  const onNotificationTypeChange = (e) => {
    const newNotificationType = e.target.checked
      ? [...state.notificationType, e.target.value]
      : state.notificationType.filter((type) => type !== e.target.value);

    setState({ ...state, notificationType: newNotificationType });
  };

  const onActionsChange = (e) => {
    const newActions = e.target.checked
      ? [...state.actions, e.target.value]
      : state.actions.filter((action) => action !== e.target.value);

    setState({ ...state, actions: newActions });
  };

  const onChangeUserIdsSelected = (selectedUsers) => {
    setState({
      ...state,
      users: selectedUsers.map((u) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phone: u.phone,
      })),
    });
  };

  const onUpdatePmNotifications = async () => {
    const jsonObject = {
      notificationType: state.notificationType,
      actions: state.actions,
    };

    if (jsonObject.notificationType.length === 0) {
      AppStore.addError('Please select how to be notified');
      return;
    }

    if (jsonObject.actions.length === 0) {
      AppStore.addError('Please select what threshold to be notified at');
      return;
    }

    if (state.users.length === 0) {
      AppStore.addError('Please select user from the user list');
      return;
    }

    try {
      setState({ ...state, saving: true });
      AppStore.addLoading('Saving');

      let timezone = CurrentUserStore.user.settings.timezone;
      if (_.isNil(timezone)) {
        timezone = 'America/Toronto';
      }

      const shopNotifications = toJS(
        ShopStore.shopNotifications.get(ShopStore.currentShop.id)
      );
      const pmAlertsNotifications = _.chain(shopNotifications)
        .get('data.emailConfiguration')
        .filter((a) => a.email_report_type === 'PmAlerts')
        .value();

      for (const user of state.users) {
        const userNotifications = pmAlertsNotifications.filter(
          (a) => a.id_user === user.id
        );
        if (_.isEmpty(userNotifications)) {
          await ShopStore.sendPmAlertsConfig(
            ShopStore.currentShop.id,
            [user],
            moment(),
            timezone,
            jsonObject
          );
        } else {
          await ShopStore.updatePmAlertsConfig(
            userNotifications[0].id,
            ShopStore.currentShop.id,
            [user],
            jsonObject
          );
        }
        await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);

        setSavedState({
          notificationType: state.notificationType,
          actions: state.actions,
          users: state.users,
          saving: false,
        });

        AppStore.openModals.set(ADD_PM_NOTIFICATIONS_MODAL_ID, false);
        AppStore.openModals.set(PM_ALLERT_SUCCESS_MODAL_ID, true);
      }
    } catch (err) {
      AppStore.addError(`Error in updating Pm alerts: ${err.message}`);
    } finally {
      setState((prevState) => ({ ...prevState, saving: false }));
      AppStore.removeLoading('Saving');
    }
  };

  useEffect(() => {
    if (AppStore.openModals.get(modalId)) {
      setState({ ...savedState, saving: false });
    }
  }, [AppStore.openModals.get(modalId)]);

  return (
    <>
      <SuccessModal modalId={PM_ALLERT_SUCCESS_MODAL_ID}>
        <Row gutter={[16, 16]} align="middle" style={{ textAlign: 'center' }}>
          <Col span={24}>
            <h5>PM Alert notification configs are saved.</h5>
          </Col>
        </Row>
      </SuccessModal>
      <StyledModal
        id={modalId}
        title={title}
        width={850}
        okText="Confirm"
        footer={
          <>
            <Button
              key="cancel"
              onClick={() => {
                setState({ ...savedState, saving: false });
                if (
                  userTableRef.current &&
                  typeof userTableRef.current.clearFiltersAndCollapse ===
                    'function'
                ) {
                  userTableRef.current.clearFiltersAndCollapse();
                }
                AppStore.openModals.set(ADD_PM_NOTIFICATIONS_MODAL_ID, false);
              }}
            >
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={onUpdatePmNotifications}
              loading={state.saving}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p>
          An alert will be sent automatically whenever a vehicle reaches the
          threshold percentage selected.
        </p>

        <Row>
          <Col span={8}>
            <p style={{ fontWeight: 700 }}>Select how to be notified:</p>
            <div>
              <Checkbox
                value="Email"
                onChange={onNotificationTypeChange}
                checked={state.notificationType.includes('Email')}
              >
                Email
              </Checkbox>

              <Checkbox
                value="Text"
                onChange={onNotificationTypeChange}
                checked={state.notificationType.includes('Text')}
              >
                Text Message
              </Checkbox>
            </div>
          </Col>
          <Col span={16}>
            <p style={{ fontWeight: 700 }}>
              Select what threshold to be notified at:
            </p>
            <div>
              <Checkbox
                value="O"
                onChange={onActionsChange}
                checked={state.actions.includes('O')}
              >
                Overdue (100%+)
              </Checkbox>

              <Checkbox
                value="D"
                onChange={onActionsChange}
                checked={state.actions.includes('D')}
              >
                Due Soon (80 - 99%)
              </Checkbox>

              <Checkbox
                value="U"
                onChange={onActionsChange}
                checked={state.actions.includes('U')}
              >
                Upcoming (70 - 79%)
              </Checkbox>
            </div>
          </Col>
        </Row>

        {!AppStore.openModals.get(EDIT_ISSUE_ALLERT_MODAL_ID) && (
          <UsersTable
            type="PMnotifications_modal"
            ref={userTableRef}
            tableStore={tableStore}
            selectedUserIds={state.users.map((u) => u.id)}
            isUserSelectionEnabled={true}
            sendEmailConfiguration={() => {}}
            sendPriorityAlertsEmailConfiguration={() => {}}
            getCurrentSelectedPriorities={() => {}}
            isDateSelectionEnabled={false}
            isPrioritySelectionEnabled={false}
            enableUserSelection={() => {}}
            isVehicleSelectionEnabled={false}
            sendFleetSegmentConfiguration={() => {}}
            columnKeys={[
              'firstName',
              'lastName',
              'email',
              'phone',
              'pm_method',
              'pm_type',
            ]}
            isEditable={false}
            showSetupButton={false}
            onChangeUserIdsSelected={onChangeUserIdsSelected}
          />
        )}
      </StyledModal>
    </>
  );
};

PMNotificationsModal.propTypes = {
  title: PropTypes.string,
  modalId: PropTypes.string,
};

export default observer(PMNotificationsModal);
